import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// Aithlo coaching is for adults (v2, D58): a 16- or 17-year-old could sign up but
// never confirm 18+, so the coach could never start (aithlo-frontend#64).
const MIN_SIGNUP_AGE = 18;
// Must match backend RegisterRequest.password Field(min_length=10) in
// backend/app/models/auth_schemas.py. Keep these in sync — a mismatch sends
// the request through to a 422 with no client-side preview.
const MIN_PASSWORD_LENGTH = 10;
const ALLOWED_COUNTRIES = [
  { code: "GB", name: "United Kingdom" },
  { code: "FR", name: "France" },
];

const calculateAge = (dobString) => {
  const dob = new Date(dobString);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
};
import { authAPI, consentsAPI } from "../services/api";
import { signupConsents } from "../lib/aiConsent";
import { AcceptCheckbox, AiConsentText, StravaRetentionSummary } from "./consent/AiConsentCopy";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/errors";
import { Button, Field, InlineNote } from "./ds";
import { AuthHeading, AuthLayout, SELECT_CLASS, SwitchLink } from "./auth/AuthLayout";
import AndroidAppLink from "./auth/AndroidAppLink";
import { cn } from "../lib/utils";
import GoogleButton from "./auth/GoogleButton";

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [country, setCountry] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Which field a check's message belongs under; the rest go in the note on top.
  const [errorField, setErrorField] = useState(null);
  // The one acceptance (fresh-start spec §3): the AI coaching consent, the
  // Strava data retention, the Terms and Privacy Policy and 18+, all at once.
  const [accepted, setAccepted] = useState(false);

  const fail = (message, field = null) => { setError(message); setErrorField(field); };

  // Signed in now: record what the box covered, at the versions the server
  // names. A failure doesn't undo the account: the terms modal asks again
  // (consent/TermsModal.jsx), before the coach builds anything.
  const recordAcceptance = async () => {
    try {
      const status = await consentsAPI.getStatus().catch(() => null);
      await consentsAPI.recordBatch(signupConsents(status), true);
    } catch (err) {
      console.error("Failed to record the sign-up acceptance:", err);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    fail("");
    if (!accepted) return;

    if (password !== confirmPassword) {
      fail("Passwords do not match", "confirm");
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      fail(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`, "password");
      return;
    }

    if (!dateOfBirth) {
      fail("Please enter your date of birth", "dob");
      return;
    }

    const age = calculateAge(dateOfBirth);
    if (age === null) {
      fail("Please enter a valid date of birth", "dob");
      return;
    }
    if (age < MIN_SIGNUP_AGE) {
      fail(`Aithlo coaching is for adults: you need to be ${MIN_SIGNUP_AGE} or over to sign up.`, "age");
      return;
    }

    if (!ALLOWED_COUNTRIES.some((c) => c.code === country)) {
      fail("Aithlo is only available in the United Kingdom and France for now.", "country");
      return;
    }

    setIsLoading(true);

    try {
      const response = await authAPI.register(email, password, dateOfBirth, country);
      login({ email, id: response.access_token ? "authenticated" : null });
      await recordAcceptance();
      navigate("/calendar", { replace: true });
    } catch (err) {
      console.error("Registration error:", err);
      fail(formatApiError(err, "Registration failed. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  // The age and country rules are said up top and marked on their field.
  const banner = error && (errorField === null || errorField === "age" || errorField === "country") ? error : "";
  const dobError = errorField === "age" ? `Must be ${MIN_SIGNUP_AGE} or over` : errorField === "dob" ? error : null;

  return (
    <AuthLayout>
      <AuthHeading small title="Create your account"
        subtitle={banner ? null : "Aithlo coaching is for adults in the UK and France."} />

      <div className="flex flex-col gap-3.5">
        <form onSubmit={handleRegister} className="flex flex-col gap-3.5">
          {banner && <InlineNote tone="error" text={banner} />}
          <Field label="Email" type="email" inputMode="email" autoComplete="email" placeholder="you@example.com"
            value={email} onChange={(e) => setEmail(e.target.value)} required />

          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 sm:gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="register-country" className="type-label text-text-secondary">Country</label>
              <select id="register-country" value={country} onChange={(e) => setCountry(e.target.value)} required
                aria-invalid={errorField === "country" || undefined}
                aria-describedby={errorField === "country" ? "register-country-note" : undefined}
                className={cn(SELECT_CLASS, !country && "text-text-tertiary", errorField === "country" && "border-[1.5px] border-status-danger")}>
                <option value="" disabled>Choose</option>
                {ALLOWED_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
              {errorField === "country" && (
                <p id="register-country-note" className="m-0 type-caption text-status-danger">Not available yet</p>
              )}
            </div>
            <Field label="Date of birth" type="date" required
              max={new Date().toISOString().split("T")[0]}
              value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)}
              error={dobError} />
          </div>

          <Field label="Password" type="password" autoComplete="new-password"
            value={password} onChange={(e) => setPassword(e.target.value)} required
            hint={`At least ${MIN_PASSWORD_LENGTH} characters`}
            error={errorField === "password" ? `At least ${MIN_PASSWORD_LENGTH} characters` : null} />
          <Field label="Confirm password" type="password" autoComplete="new-password"
            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
            error={errorField === "confirm" ? "Passwords don’t match" : null} />

          <div className="flex flex-col gap-3 rounded-card border border-border-subtle bg-bg-surface p-4">
            <div className="flex max-h-48 flex-col gap-3 overflow-y-auto pr-1">
              <AiConsentText />
              <StravaRetentionSummary />
            </div>
            <AcceptCheckbox id="register-accept" checked={accepted} onChange={setAccepted} disabled={isLoading} />
          </div>

          <Button type="submit" block loading={isLoading} disabled={!accepted}>Create account</Button>
        </form>

        <GoogleButton />

        <SwitchLink prompt="Already have an account?" to="/login" label="Sign in" />
        <AndroidAppLink />
      </div>
    </AuthLayout>
  );
};

export default Register;
